import { TokenInfo } from "@/app/services/token-api";
import { embeddedWallet } from "@/embedded-wallet";
import {
  COMMITMENT,
  activeConnection,
  resolveRpcConnections,
} from "@/rpc-connection";
import {
  CU_LIMIT_UNWRAP_SOL,
  CU_LIMIT_WITHDRAW_NATIVE_SOL,
  CU_LIMIT_WITHDRAW_SPL_TOKEN,
  CU_LIMIT_WRAP_SOL,
  SOL_ADDRESS,
  SOL_DECIMALS,
  calculateComputeUnitPriceMicroLamports,
  normalizeScaled,
  wait,
} from "@/utils";
import { createAction } from "@reduxjs/toolkit";
import {
  NATIVE_MINT,
  TOKEN_PROGRAM_ID,
  createAssociatedTokenAccountIdempotentInstruction,
  createCloseAccountInstruction,
  createSyncNativeInstruction,
  createTransferInstruction,
  getAssociatedTokenAddressSync,
  unpackAccount,
  AccountLayout,
} from "@solana/spl-token";
import {
  ComputeBudgetProgram,
  Connection,
  ParsedAccountData,
  PublicKey,
  RpcResponseAndContext,
  SignatureStatus,
  SimulatedTransactionResponse,
  SystemProgram,
  TokenAccountsFilter,
  Transaction,
  TransactionError,
  TransactionInstruction,
  VersionedTransaction,
} from "@solana/web3.js";
import bs58 from "bs58";
import { type Quote } from "./aggregator-api";
import { UnauthorizedError, api } from "./api";
import { ISolana } from "@dynamic-labs/solana";
import type { TransactionStatus } from "@/app/background-transactions";
import { addTokensToCache, getTokenInfoFromCache } from "@/app/services/util";

// Websocket updates will dispatch these actions for other subscribers
export const globalUpdateWalletInfo = createAction<WalletInfo>(
  "global/update/walletInfo",
);
export const globalUpdateTokenAccountByMint = createAction<TokenAccountInfo>(
  "global/update/tokenAccountByMint",
);
export const updateTransactionStatus = createAction<{
  signature: string;
  status: TransactionStatus;
}>("walletApi/updateTransactionStatus");

export const walletApi = api.injectEndpoints({
  endpoints: (builder) => ({
    walletInfo: builder.query<WalletInfo, void>({
      async queryFn(_, { dispatch }) {
        try {
          // First we wait for the connection to be initialized
          const connection = await activeConnection.getReadConnection();
          if (!embeddedWallet.address) {
            return {
              error: {
                status: 400,
                data: "Wallet not connected",
              },
            };
          }
          const balance = await connection.getBalance(
            new PublicKey(embeddedWallet.address),
          );

          const data = {
            balance: balance
              ? Number(normalizeScaled(balance.toString(), SOL_DECIMALS))
              : null,
            rawBalance: balance ? balance.toString() : null,
            walletAddress: embeddedWallet.address,
          };
          dispatch(globalUpdateWalletInfo(data));
          return {
            data,
          };
        } catch (err) {
          return handleError(err, {
            status: 500,
            data: "Error getting wallet balance",
          });
        }
      },
      providesTags: ["WalletBalance"],
      async onCacheEntryAdded(
        _arg,
        { dispatch, updateCachedData, cacheDataLoaded, cacheEntryRemoved },
      ) {
        // Once the cache entry is added, we subscribe to account changes with the connection websocket
        //? https://redux-toolkit.js.org/rtk-query/usage/streaming-updates#websocket-chat-api
        await cacheDataLoaded;
        const connection = await activeConnection.getReadConnection();
        let subscriptionId: number | null = null;
        try {
          if (!embeddedWallet.address) return;
          const walletPubkey = new PublicKey(embeddedWallet.address);
          subscriptionId = connection.onAccountChange(
            walletPubkey,
            (accountInfo, _context) => {
              updateCachedData((data) => {
                data.rawBalance = accountInfo.lamports.toString();
                data.balance = Number(
                  normalizeScaled(
                    accountInfo.lamports.toString(),
                    SOL_DECIMALS,
                  ),
                );
                dispatch(globalUpdateWalletInfo(data));
              });
            },
          );
        } catch {
          console.error("Failed to subscribe to account changes");
        }

        await cacheEntryRemoved;
        if (subscriptionId) {
          void connection.removeAccountChangeListener(subscriptionId);
        }
      },
    }),
    tokenAccounts: builder.query<TokenAccountInfo[], void>({
      async queryFn() {
        try {
          const connection = await activeConnection.getReadConnection();
          if (!embeddedWallet.address) {
            return { data: [] };
          }
          const pubkey = new PublicKey(embeddedWallet.address);

          const filter: TokenAccountsFilter = {
            programId: TOKEN_PROGRAM_ID,
          };

          const accounts = await connection.getParsedTokenAccountsByOwner(
            pubkey,
            filter,
          );

          const data = accounts.value
            .filter((x) => {
              // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
              const mint = new PublicKey(x.account.data.parsed.info.mint);
              const associatedTokenAccountAddress =
                getAssociatedTokenAddressSync(mint, pubkey);
              return associatedTokenAccountAddress.equals(x.pubkey);
            })
            .map((account) => {
              // prettier-ignore
              {
                const address = account.pubkey.toBase58();
                const parsedAccountInfo = account.account.data
                /* eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access */
                const mintAddress: string = parsedAccountInfo.parsed.info.mint;
                /* eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access */
                const tokenBalance: number = parsedAccountInfo.parsed.info.tokenAmount.uiAmount;
                // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-assignment
                const tokenRawBalance: string = parsedAccountInfo.parsed.info.tokenAmount.amount;
                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
                const decimals: number = parsedAccountInfo.parsed.info.tokenAmount.decimals;
                return {
                  address,
                  mintAddress,
                  tokenBalance,
                  tokenRawBalance,
                  decimals,
                };
              }
            });
          return { data };
        } catch (error) {
          return { error: { status: 500, data: (error as Error).message } };
        }
      },
      providesTags: ["TokenAccounts"],
      async onCacheEntryAdded(_arg, { getState, dispatch, cacheDataLoaded }) {
        const { data } = await cacheDataLoaded;
        const tokens: TokenInfo[] = data
          .filter(
            (x) =>
              x.decimals !== undefined &&
              // This token info data is incomplete so we
              // only add this info if it's not in the cache
              getTokenInfoFromCache(getState, x.mintAddress) === undefined,
          )
          .map((x) => ({
            address: x.mintAddress,
            // decimals must be defined via filter
            decimals: x.decimals ?? 0,
            verified: false,
          }));
        addTokensToCache(dispatch, tokens);
      },
    }),
    tokenAccountByMint: builder.query<
      TokenAccountInfo | null,
      string | undefined
    >({
      async queryFn(mintAddress, { dispatch }) {
        try {
          const connection = await activeConnection.getReadConnection();
          if (!embeddedWallet.address || !mintAddress) {
            return { data: null };
          }
          const ownerPubkey = new PublicKey(embeddedWallet.address);
          const mintPubkey = new PublicKey(mintAddress);
          const associatedTokenAddress = getAssociatedTokenAddressSync(
            mintPubkey,
            ownerPubkey,
          );
          const response = await connection.getParsedAccountInfo(
            associatedTokenAddress,
          );
          const parsedAccountInfo = response.value?.data as
            | ParsedAccountData
            | undefined;
          if (!parsedAccountInfo) {
            dispatch(
              globalUpdateTokenAccountByMint({
                mintAddress,
                tokenBalance: 0,
                tokenRawBalance: "0",
              }),
            );
            return { data: null };
          }

          let data: TokenAccountInfo;
          // prettier-ignore
          {
            const address: string = associatedTokenAddress.toBase58();
            /* eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access */
            const mintAddress: string = parsedAccountInfo.parsed.info.mint;
            /* eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access */
            const tokenBalance: number = parsedAccountInfo.parsed.info.tokenAmount.uiAmount;
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-assignment
            const tokenRawBalance: string = parsedAccountInfo.parsed.info.tokenAmount.amount;
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
            const decimals: number = parsedAccountInfo.parsed.info.tokenAmount.decimals;
            data = {
              address,
              mintAddress,
              tokenBalance,
              tokenRawBalance,
              decimals,
            };
          }
          dispatch(globalUpdateTokenAccountByMint(data));
          return { data };
        } catch {
          return {
            error: {
              status: 500,
              data: "Error getting token account",
            },
          };
        }
      },
      providesTags: (result, _error, _mintAddress) => [
        { type: "TokenAccountByMint", id: result?.address ?? "unknown" },
      ],
      async onCacheEntryAdded(
        arg,
        { dispatch, updateCachedData, cacheDataLoaded, cacheEntryRemoved },
      ) {
        await cacheDataLoaded;
        const connection = await activeConnection.getReadConnection();
        const tokenAccountAddress = (await cacheDataLoaded).data?.address;
        if (!tokenAccountAddress) return;

        let subscriptionId: number | null = null;
        try {
          if (!embeddedWallet.address) return;
          const pubkey = new PublicKey(tokenAccountAddress);
          subscriptionId = connection.onAccountChange(
            pubkey,
            (accountInfo, _context) => {
              try {
                const parsedAccountInfo = unpackAccount(pubkey, accountInfo);
                updateCachedData((data) => {
                  const decimals = data?.decimals;
                  if (!decimals) return;
                  data.tokenBalance = Number(
                    normalizeScaled(parsedAccountInfo.amount, decimals),
                  );
                  data.tokenRawBalance = parsedAccountInfo.amount.toString();
                  dispatch(globalUpdateTokenAccountByMint(data));
                });
              } catch {
                if (!arg) return;
                dispatch(
                  globalUpdateTokenAccountByMint({
                    tokenBalance: 0,
                    tokenRawBalance: "0",
                    mintAddress: arg,
                  }),
                );
                updateCachedData((data) => {
                  if (!data) return;
                  data.tokenBalance = 0;
                  data.tokenRawBalance = "0";
                });
              }
            },
          );
        } catch {
          console.error("Failed to subscribe to account changes");
        }

        await cacheEntryRemoved;
        if (subscriptionId) {
          void connection.removeAccountChangeListener(subscriptionId);
        }
      },
    }),
    destinationSolBalance: builder.query<{ rawBalance: string }, string>({
      async queryFn(destinationAddress) {
        try {
          const connection = await activeConnection.getReadConnection();
          const balance = await connection.getBalance(
            new PublicKey(destinationAddress),
          );
          return {
            data: { rawBalance: balance.toString() },
          };
        } catch (error) {
          return {
            error: {
              status: 500,
              data: (error as Error).message,
            },
          };
        }
      },
      providesTags: (_r, _e, destinationAddress) => [
        {
          type: "DestinationSolBalance",
          id: destinationAddress,
        },
      ],
    }),
    destinationTokenBalance: builder.query<
      { rawBalance: string; address: string } | null,
      { destinationAddress: string; mintAddress: string }
    >({
      async queryFn({ destinationAddress, mintAddress }) {
        try {
          const ownerPubkey = new PublicKey(destinationAddress);
          const mintPubkey = new PublicKey(mintAddress);
          const associatedTokenAddress = getAssociatedTokenAddressSync(
            mintPubkey,
            ownerPubkey,
          );
          const connection = await activeConnection.getReadConnection();
          const response = await connection.getParsedAccountInfo(
            associatedTokenAddress,
          );
          const parsedAccountInfo = response.value?.data as
            | ParsedAccountData
            | undefined;
          if (!parsedAccountInfo) {
            return { data: null };
          }
          // prettier-ignore
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-assignment
          const tokenRawBalance: string = parsedAccountInfo.parsed.info.tokenAmount.amount;

          return {
            data: {
              rawBalance: tokenRawBalance,
              address: associatedTokenAddress.toBase58(),
            },
          };
        } catch (error) {
          return {
            error: {
              status: 500,
              data: (error as Error).message,
            },
          };
        }
      },
      providesTags: (result) => [
        {
          type: "DestinationTokenBalance",
          id: result?.address,
        },
      ],
    }),
    tokenAccountsByOwner: builder.query<
      { address: string; mintAddress: string; balance: string }[],
      string
    >({
      async queryFn(ownerAddress) {
        try {
          const connection = await activeConnection.getReadConnection();
          const response = await connection.getTokenAccountsByOwner(
            new PublicKey(ownerAddress),
            {
              programId: TOKEN_PROGRAM_ID,
            },
          );
          const data = response.value.map((info) => {
            const accountInfo = AccountLayout.decode(info.account.data);
            return {
              address: info.pubkey.toBase58(),
              mintAddress: accountInfo.mint.toBase58(),
              balance: accountInfo.amount.toString(),
            };
          });

          return { data };
        } catch (error) {
          return {
            error: {
              status: 500,
              data: (error as Error).message,
            },
          };
        }
      },
    }),
    changeWallet: builder.mutation<null, void>({
      queryFn: async () => {
        // This just needs to invalidate the wallet queries once the wallet changes
        await wait(100);
        return { data: null };
      },
      invalidatesTags: ["WalletBalance", "TokenAccounts", "TokenAccountByMint"],
    }),
    signTransaction: builder.mutation<
      VersionedTransaction,
      VersionedTransaction
    >({
      queryFn: async (
        transaction,
      ): Promise<
        MutationResult<
          VersionedTransaction,
          BuildAndSendTransferTransactionError
        >
      > => {
        try {
          await signTransaction(transaction);
          return {
            data: transaction,
          };
        } catch (error) {
          const e = error as Error | undefined;

          if (e?.message === "cancel_signature") {
            return {
              error: { status: 500, kind: "cancel_signature", data: error },
            };
          }

          return {
            error: { status: 500, kind: "failed_to_sign", data: error },
          };
        }
      },
    }),
    sendTransaction: builder.mutation<
      SendAndConfirmTransactionResult,
      SendTransactionArgs
    >({
      queryFn: async (
        { signature, transaction },
        { dispatch },
      ): Promise<
        MutationResult<SendAndConfirmTransactionResult, SendTransactionError>
      > => {
        try {
          const { read, write } = await resolveRpcConnections();
          const onSendComplete = () => {
            setTimeout(() => {
              dispatch(
                updateTransactionStatus({ signature, status: "confirming" }),
              );
            }, 1000);
          };

          const result = await sendAndConfirmTransaction(
            read,
            write,
            transaction,
            { onSendComplete },
          );
          return { data: result };
        } catch (error) {
          return {
            error: {
              status: 500,
              kind: "unhandled_error",
              data: error,
            },
          };
        }
      },
      invalidatesTags: ["TokenAccounts"],
    }),
    buildAndSendTransferTransaction: builder.mutation<
      SendAndConfirmTransactionResult,
      BuildTransferTransactionParams
    >({
      queryFn: async (
        params,
      ): Promise<
        MutationResult<
          SendAndConfirmTransactionResult,
          BuildAndSendTransferTransactionError
        >
      > => {
        try {
          const { read, write } = await resolveRpcConnections();
          const transaction = buildTransferTransaction(params);

          try {
            const blockhash = await read.getLatestBlockhash();
            transaction.recentBlockhash = blockhash.blockhash;
          } catch (error) {
            return {
              error: {
                status: 500,
                kind: "failed_to_fetch_latest_blockhash",
                data: error,
              },
            };
          }

          try {
            await signTransaction(transaction);
          } catch (error) {
            const e = error as Error | undefined;

            if (e?.message === "cancel_signature") {
              return {
                error: { status: 500, kind: "cancel_signature", data: error },
              };
            }

            return {
              error: { status: 500, kind: "failed_to_sign", data: error },
            };
          }

          const result = await sendAndConfirmTransaction(
            read,
            write,
            transaction,
          );
          return { data: result };
        } catch (error) {
          return {
            error: {
              status: 500,
              kind: "unhandled_error",
              data: error,
            },
          };
        }
      },
      invalidatesTags: [
        "TokenAccounts",
        "DestinationSolBalance",
        "DestinationTokenBalance",
      ],
    }),
    wrapSol: builder.mutation<SendAndConfirmTransactionResult, WrapSolParams>({
      async queryFn({
        walletAddress,
        amountLamports,
        prioritizationFeeLamports,
      }): Promise<
        MutationResult<SendAndConfirmTransactionResult, WrapSolError>
      > {
        try {
          const { read, write } = await resolveRpcConnections();

          const userPublicKey = new PublicKey(walletAddress);
          const wsolTokenAccountAddress = getAssociatedTokenAddressSync(
            NATIVE_MINT,
            userPublicKey,
          );

          const transaction = new Transaction().add(
            ComputeBudgetProgram.setComputeUnitLimit({
              units: CU_LIMIT_WRAP_SOL,
            }),
            ComputeBudgetProgram.setComputeUnitPrice({
              microLamports: calculateComputeUnitPriceMicroLamports(
                prioritizationFeeLamports,
                CU_LIMIT_WRAP_SOL,
              ),
            }),
            createAssociatedTokenAccountIdempotentInstruction(
              userPublicKey,
              wsolTokenAccountAddress,
              userPublicKey,
              NATIVE_MINT,
            ),
            SystemProgram.transfer({
              fromPubkey: userPublicKey,
              toPubkey: wsolTokenAccountAddress,
              lamports: BigInt(amountLamports),
            }),
            createSyncNativeInstruction(wsolTokenAccountAddress),
          );
          transaction.feePayer = userPublicKey;

          try {
            const blockhash = await read.getLatestBlockhash();
            transaction.recentBlockhash = blockhash.blockhash;
          } catch (error) {
            return {
              error: {
                status: 500,
                kind: "failed_to_fetch_latest_blockhash",
                data: error,
              },
            };
          }

          try {
            await signTransaction(transaction);
          } catch (error) {
            const e = error as Error | undefined;

            if (e?.message === "cancel_signature") {
              return {
                error: { status: 500, kind: "cancel_signature", data: error },
              };
            }

            return {
              error: { status: 500, kind: "failed_to_sign", data: error },
            };
          }

          try {
            const simulationResult =
              await read.simulateTransaction(transaction);
            if (simulationResult.value.err) {
              return {
                error: {
                  status: 409,
                  kind: "simulation_failed",
                  data: simulationResult,
                },
              };
            }
          } catch (error) {
            return {
              error: {
                status: 500,
                kind: "not_simulated",
                data: error,
              },
            };
          }

          const result = await sendAndConfirmTransaction(
            read,
            write,
            transaction,
          );

          return { data: result };
        } catch (error) {
          return {
            error: {
              status: 500,
              kind: "unhandled_error",
              data: error,
            },
          };
        }
      },
      invalidatesTags: ["TokenAccounts"],
    }),
    unwrapSol: builder.mutation<
      SendAndConfirmTransactionResult,
      UnwrapSolParams
    >({
      async queryFn({
        walletAddress,
        prioritizationFeeLamports,
      }): Promise<
        MutationResult<SendAndConfirmTransactionResult, UnwrapSolError>
      > {
        try {
          const { read, write } = await resolveRpcConnections();

          const userPublicKey = new PublicKey(walletAddress);
          const wsolTokenAccountAddress = getAssociatedTokenAddressSync(
            NATIVE_MINT,
            userPublicKey,
          );

          const transaction = new Transaction().add(
            ComputeBudgetProgram.setComputeUnitLimit({
              units: CU_LIMIT_UNWRAP_SOL,
            }),
            ComputeBudgetProgram.setComputeUnitPrice({
              microLamports: calculateComputeUnitPriceMicroLamports(
                prioritizationFeeLamports,
                CU_LIMIT_UNWRAP_SOL,
              ),
            }),
            createCloseAccountInstruction(
              wsolTokenAccountAddress,
              userPublicKey,
              userPublicKey,
            ),
          );
          transaction.feePayer = userPublicKey;

          try {
            const blockhash = await read.getLatestBlockhash();
            transaction.recentBlockhash = blockhash.blockhash;
          } catch (error) {
            return {
              error: {
                status: 500,
                kind: "failed_to_fetch_latest_blockhash",
                data: error,
              },
            };
          }

          try {
            await signTransaction(transaction);
          } catch (error) {
            const e = error as Error | undefined;

            if (e?.message === "cancel_signature") {
              return {
                error: { status: 500, kind: "cancel_signature", data: error },
              };
            }

            return {
              error: { status: 500, kind: "failed_to_sign", data: error },
            };
          }

          try {
            const simulationResult =
              await read.simulateTransaction(transaction);
            if (simulationResult.value.err) {
              return {
                error: {
                  status: 409,
                  kind: "simulation_failed",
                  data: simulationResult,
                },
              };
            }
          } catch (error) {
            return {
              error: {
                status: 500,
                kind: "not_simulated",
                data: error,
              },
            };
          }

          const result = await sendAndConfirmTransaction(
            read,
            write,
            transaction,
          );
          return { data: result };
        } catch (error) {
          return {
            error: {
              status: 500,
              kind: "unhandled_error",
              data: error,
            },
          };
        }
      },
      invalidatesTags: ["TokenAccounts"],
    }),
    latestBlockHeight: builder.query<number, void>({
      async queryFn() {
        try {
          const connection = await activeConnection.getReadConnection();
          const height = await connection.getBlockHeight();
          return { data: height };
        } catch (error) {
          return {
            error: {
              status: 500,
              kind: "failed_to_fetch_latest_blockhash",
              data: (error as Error).message,
            },
          };
        }
      },
    }),
  }),
});

interface WalletApiError {
  type?: "unknown" | "timeout" | "transactionFailed" | "unauthorized";
  status: number;
  data: string;
}

function handleError(
  error: unknown,
  forwardedError: WalletApiError,
): { error: WalletApiError } {
  console.error(error);
  if (error instanceof UnauthorizedError) {
    return {
      error: {
        status: 401,
        data: "Wallet not connected",
      },
    };
  }

  if (
    (error as Error | undefined)?.message.includes(
      "The operation either timed out or was not allowed",
    )
  ) {
    return {
      error: {
        status: 422,
        data: "The operation either timed out or was not allowed",
      },
    };
  }
  return { error: forwardedError };
}

export const {
  useWalletInfoQuery,
  useLazyWalletInfoQuery,
  useTokenAccountsQuery,
  useTokenAccountByMintQuery,
  useChangeWalletMutation,
  useSignTransactionMutation,
  useSendTransactionMutation,
  useBuildAndSendTransferTransactionMutation,
  useWrapSolMutation,
  useUnwrapSolMutation,
  useDestinationSolBalanceQuery,
  useDestinationTokenBalanceQuery,
  useLatestBlockHeightQuery,
  useTokenAccountsByOwnerQuery,
} = walletApi;

export async function signTransaction(
  transaction: Transaction | VersionedTransaction,
): Promise<void> {
  const connector = await embeddedWallet.getConnector();
  if (!connector) {
    throw new UnauthorizedError("Connector not found");
  }

  try {
    const signer = await connector.getSigner<ISolana>();
    await signer.signTransaction(transaction);
  } catch (error) {
    const e = error as Error | undefined;
    if (
      // chrome
      e?.message.includes(
        "The operation either timed out or was not allowed",
      ) ||
      // firefix
      e?.message.includes("The operation was aborted.") ||
      // safari
      e?.message.includes("This request has been cancelled by the user")
    ) {
      throw new Error("cancel_signature");
    }

    throw error;
  }
}

export function buildTransferTransaction({
  from,
  to,
  transfer,
  priorityFee,
}: BuildTransferTransactionParams): Transaction {
  const fromKey = new PublicKey(from);
  const toKey = new PublicKey(to);
  const instructions: TransactionInstruction[] = [];
  const isFullWithdrawal = transfer.amount === transfer.balance;

  if (transfer.tokenAddress === SOL_ADDRESS && transfer.symbol === "SOL") {
    const cuLimit = CU_LIMIT_WITHDRAW_NATIVE_SOL;
    const cuPriceMicroLamports = calculateComputeUnitPriceMicroLamports(
      priorityFee.toString(),
      cuLimit,
    );

    instructions.push(
      ...[
        ComputeBudgetProgram.setComputeUnitLimit({ units: cuLimit }),
        ComputeBudgetProgram.setComputeUnitPrice({
          microLamports: cuPriceMicroLamports,
        }),
        SystemProgram.transfer({
          fromPubkey: fromKey,
          toPubkey: toKey,
          lamports: transfer.amount,
        }),
      ],
    );
  } else {
    const tokenMint = new PublicKey(transfer.tokenAddress);
    const source = getAssociatedTokenAddressSync(tokenMint, fromKey);
    const dest = getAssociatedTokenAddressSync(tokenMint, toKey);

    const cuLimit = CU_LIMIT_WITHDRAW_SPL_TOKEN;
    const cuPriceMicroLamports = calculateComputeUnitPriceMicroLamports(
      priorityFee.toString(),
      cuLimit,
    );

    instructions.push(
      ...[
        ComputeBudgetProgram.setComputeUnitLimit({ units: cuLimit }),
        ComputeBudgetProgram.setComputeUnitPrice({
          microLamports: cuPriceMicroLamports,
        }),
        createAssociatedTokenAccountIdempotentInstruction(
          fromKey,
          dest,
          toKey,
          tokenMint,
        ),
        createTransferInstruction(source, dest, fromKey, transfer.amount),
      ],
    );
    if (isFullWithdrawal) {
      instructions.push(
        createCloseAccountInstruction(source, fromKey, fromKey),
      );
    }
  }

  const transferTransaction = new Transaction().add(...instructions);
  transferTransaction.feePayer = fromKey;

  return transferTransaction;
}

export interface SendTransactionArgs {
  signature: string;
  transaction: VersionedTransaction | Transaction;
  lastValidBlockHeight: number;
  swapPair?: { base: TokenInfo; quote: TokenInfo };
  quote?: Quote;
  priorityFeeUsed: number;
}

interface BaseError {
  status: number;
  data: unknown;
}

type MutationResult<
  TOk,
  TErr extends { status: number; kind: string; data: unknown },
> =
  | { data: TOk }
  | {
      error: TErr;
    };

export type SendAndConfirmTransactionResult =
  | SendAndConfirmTransactionResultSuccess
  | SendAndConfirmTransactionResultFailed
  | SendAndConfirmTransactionResultExpired
  | SendAndConfirmTransactionResultUnknown;

interface SendAndConfirmTransactionResultBase {
  signature: string;
}

interface SendAndConfirmTransactionResultSuccess
  extends SendAndConfirmTransactionResultBase {
  status: "success";
}

interface SendAndConfirmTransactionResultFailed
  extends SendAndConfirmTransactionResultBase {
  status: "failed";
  transactionError: TransactionError;
}

interface SendAndConfirmTransactionResultExpired
  extends SendAndConfirmTransactionResultBase {
  status: "expired";
}

interface SendAndConfirmTransactionResultUnknown
  extends SendAndConfirmTransactionResultBase {
  status: "unknown";
}

/** Transaction building error types */
type BuildTransactionError = FailedToFetchLatestBlockhash;

/** Web3Auth instance not ready */
interface Web3authNotInitializedError extends BaseError {
  kind: "web3auth_not_initialized";
}

/** Failed to fetch blockhash for the transaction */
interface FailedToFetchLatestBlockhash extends BaseError {
  kind: "failed_to_fetch_latest_blockhash";
}

type SignError = FailedToSignError | CancelSignatureError;

interface CancelSignatureError extends BaseError {
  kind: "cancel_signature";
}

/** An error occurred when attempting to sign the transaction */
interface FailedToSignError extends BaseError {
  kind: "failed_to_sign";
}

/** Transaction simulation error types */
type SimulateError = SimulateErrorNotSimulated | SimulateErrorSimulationFailed;

/** Transaction was not simulated. The simulation request failed. */
interface SimulateErrorNotSimulated extends BaseError {
  kind: "not_simulated";
}

/** Transaction was simulated and failed in simulation  */
interface SimulateErrorSimulationFailed extends BaseError {
  kind: "simulation_failed";
  data: RpcResponseAndContext<SimulatedTransactionResponse>;
}

interface UnhandledError extends BaseError {
  kind: "unhandled_error";
}

type Web3authBuildAndSendTransferTransactionError =
  | Web3authNotInitializedError
  | BuildTransactionError
  | SignError
  | SimulateError
  | UnhandledError;

export function isWeb3authBuildAndSendTransactionError(
  error: unknown,
): error is Web3authBuildAndSendTransferTransactionError {
  return typeof error === "object" && error !== null && "kind" in error;
}

type SendTransactionError = UnhandledError;

export function isSendTransactionError(
  error: unknown,
): error is SendTransactionError {
  return typeof error === "object" && error !== null && "kind" in error;
}

type BuildAndSendTransferTransactionError =
  | BuildTransactionError
  | SignError
  | SimulateError
  | UnhandledError;

export function isBuildAndSendTransactionError(
  error: unknown,
): error is BuildAndSendTransferTransactionError {
  return typeof error === "object" && error !== null && "kind" in error;
}

type SignTransactionError = SignError | UnhandledError;

export function isSignTransactionError(
  error: unknown,
): error is SignTransactionError {
  return typeof error === "object" && error !== null && "kind" in error;
}

type WrapSolError =
  | BuildTransactionError
  | SignError
  | SimulateError
  | UnhandledError;

export function isWrapSolError(error: unknown): error is WrapSolError {
  return typeof error === "object" && error !== null && "kind" in error;
}

type UnwrapSolError =
  | BuildTransactionError
  | SignError
  | SimulateError
  | UnhandledError;

export function isUnwrapSolError(error: unknown): error is UnwrapSolError {
  return typeof error === "object" && error !== null && "kind" in error;
}

export interface SendTransactionCallbacks {
  onSendComplete?: () => void;
}

export async function sendAndConfirmTransaction(
  readConnection: Connection,
  writeConnections: Connection[],
  transaction: Transaction | VersionedTransaction,
  { onSendComplete }: SendTransactionCallbacks = {},
): Promise<SendAndConfirmTransactionResult> {
  const CONFIRM_AFTER_EXPIRE_DELAY = 10_000;

  const rawSignature =
    transaction instanceof Transaction
      ? transaction.signature
      : transaction.signatures[0];
  if (!rawSignature || rawSignature.length === 0) {
    throw new Error("Transaction not signed");
  }
  const signature = bs58.encode(rawSignature);
  console.log(`Sending and confirming transaction ${signature}`);

  const abortController = new AbortController();
  const transactionStatusCheckTask = subscribeToSignature(
    readConnection,
    signature,
    abortController.signal,
  );

  const transactionBroadcastTask = broadcastTransaction(
    writeConnections,
    transaction,
    abortController.signal,
    { onSendComplete },
  );
  const result = await Promise.any([
    transactionStatusCheckTask,
    transactionBroadcastTask,
  ]);
  if (result.kind === "SubscribeToSignature") {
    // Transaction reached the desired commitment level. Stop all other tasks and return transaction
    // status to caller.
    abortController.abort();
    console.info(`Transaction signature: ${signature}`);
    if (result.result.error) {
      return {
        signature,
        status: "failed",
        transactionError: result.result.error,
      };
    }
    return { signature, status: "success" };
  }

  // Broadcast finished first. Wait for the transaction to reach the desired commitment level or
  // expire.
  const transactionExpiryCheckTask = waitForTransactionToExpire(
    transaction,
    readConnection,
    abortController.signal,
  );
  // Time out after 2 minutes so that this doesn't run forever if the expiry check task fails
  const timeoutTask = waitForTimeout(120_000);
  const result2 = await Promise.any([
    transactionStatusCheckTask,
    transactionExpiryCheckTask,
    timeoutTask,
  ]);
  if (result2.kind === "SubscribeToSignature") {
    // Transaction reached the desired commitment level. Stop all other tasks and return transaction
    // status to caller.
    abortController.abort();
    console.info(`Transaction signature: ${signature}`);
    if (result2.result.error) {
      return {
        signature,
        status: "failed",
        transactionError: result2.result.error,
      };
    }
    return { signature, status: "success" };
  } else if (result2.kind === "WaitForTimeout") {
    abortController.abort();
    return { signature, status: "unknown" };
  }

  // Transaction expired. Check one last time for transaction status and return to caller.
  // Depending on MAX_RETRIES and sendTransaction interval, the transaction might still be valid
  // after MAX_RETRIES attempts. Similarly, the transaction might not yet have been propagated to
  // the read RPC node, the read node might be behind the write nodes, or the transaction status
  // subscription might've failed. To ensure we don't erroneously report that the transaction
  // expired/timed out in these cases, we wait for the transaction to expire before checking the
  // status one last time.
  const result3 = await Promise.any([
    transactionStatusCheckTask,
    wait(CONFIRM_AFTER_EXPIRE_DELAY),
  ]);
  // Check subscription result first
  if (result3?.kind === "SubscribeToSignature") {
    // Transaction reached the desired commitment level. Stop all other tasks and return transaction
    // status to caller.
    abortController.abort();
    console.info(`Transaction signature: ${signature}`);
    if (result3.result.error) {
      return {
        signature,
        status: "failed",
        transactionError: result3.result.error,
      };
    }
    return { signature, status: "success" };
  }

  // Stop all tasks that might still be running
  abortController.abort();

  // Check via getSignatureStatus in case the subscription failed
  try {
    const status = await readConnection.getSignatureStatus(signature);
    if (isStatusConfirmed(status)) {
      console.info(`Transaction signature: ${signature}`);
      if (status.value.err) {
        return {
          signature,
          status: "failed",
          transactionError: status.value.err,
        };
      }
      return { signature, status: "success" };
    } else {
      return { signature, status: "expired" };
    }
  } catch (e) {
    console.error("Error calling getSignatureStatus", e);
    return { signature, status: "unknown" };
  }
}

interface BroadcastTransactionResult {
  kind: "BroadcastTransaction";
}

/** Broadcasts the transaction on the provided connections until it has broadcasted attempted to
 * broadcast MAX_RETRIES times, all connections have reported that the transaction's blockhash is
 * invalid, or the abortSignal is raised. */
async function broadcastTransaction(
  connections: Connection[],
  transaction: Transaction | VersionedTransaction,
  abortSignal: AbortSignal,
  { onSendComplete }: SendTransactionCallbacks = {},
): Promise<BroadcastTransactionResult> {
  const connectionStatus = connections.map(() => ({
    ready: true,
    hasReturnedBlockhashNotFound: false,
  }));

  const MAX_RETRIES = 75;
  const SEND_TRANSACTION_INTERVAL = 1000;
  const BLOCKHASH_NOT_FOUND_GRACE_DELAY = 10_000;

  const transactionBuffer = transaction.serialize();
  const sendTransactionStartTime = performance.now();
  let isSendCompleted = false;

  for (let attempt = 0; attempt <= MAX_RETRIES; attempt += 1) {
    const connectionIdx = attempt % connections.length;

    if (!connectionStatus[connectionIdx].ready) {
      console.info(`Connection ${connectionIdx + 1} is not ready`);
      await wait(SEND_TRANSACTION_INTERVAL);
      if (abortSignal.aborted) {
        return { kind: "BroadcastTransaction" };
      }
      continue;
    }

    const connection = connections[connectionIdx];
    try {
      await connection.sendRawTransaction(transactionBuffer, {
        skipPreflight: true,
        preflightCommitment: COMMITMENT,
        maxRetries: 0,
      });
      if (abortSignal.aborted) {
        return { kind: "BroadcastTransaction" };
      }
      // If the sendTransaction call succeeded, it has found the blockhash
      connectionStatus[connectionIdx].hasReturnedBlockhashNotFound = false;
      if (!isSendCompleted) {
        isSendCompleted = true;
        onSendComplete?.();
      }
    } catch (e) {
      console.error("Error calling sendTransaction", e);
      if (
        e instanceof Error &&
        e.message.includes("Blockhash not found") &&
        performance.now() - sendTransactionStartTime >
          BLOCKHASH_NOT_FOUND_GRACE_DELAY
      ) {
        connectionStatus[connectionIdx].hasReturnedBlockhashNotFound = true;
      }
      if (hasRetryAfterMs(e)) {
        connectionStatus[connectionIdx].ready = false;
        setTimeout(() => {
          connectionStatus[connectionIdx].ready = true;
        }, e.retryAfterMs);
      }
      // Then, try the next connection after confirm checks
    }

    if (connectionStatus.every((x) => x.hasReturnedBlockhashNotFound)) {
      // All connections have returned blockhash not found after the grace
      // period. Stop trying to broadcast the transaction.
      return { kind: "BroadcastTransaction" };
    }

    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (SEND_TRANSACTION_INTERVAL > 0) {
      await wait(SEND_TRANSACTION_INTERVAL);
      if (abortSignal.aborted) {
        return { kind: "BroadcastTransaction" };
      }
    }
  }

  return { kind: "BroadcastTransaction" };
}

interface SubscribeToSignatureResult {
  kind: "SubscribeToSignature";
  result: SignatureSubscribeResultProcessed;
}
interface SignatureSubscribeResultProcessed {
  isProcessed: true;
  slot: number;
  error: TransactionError | null;
}

/** Resolves with the slot in which the transaction was processed when the provided signature
 * reaches the connection's default commitment level. Resolves with `null` if the abortSignal is
 * raised before the transaction reaches the commitment level. */
function subscribeToSignature(
  connection: Connection,
  signature: string,
  abortSignal: AbortSignal,
): Promise<SubscribeToSignatureResult> {
  interface SignatureSubscribeResult {
    result:
      | SignatureSubscribeResultProcessed
      | SignatureSubscribeResultNotProcessed;
  }
  interface SignatureSubscribeResultNotProcessed {
    isProcessed: false;
  }
  const signatureSubscribeResult: SignatureSubscribeResult = {
    result: {
      isProcessed: false,
    },
  };

  const signatureSubscriptionId = connection.onSignature(
    signature,
    (signatureResult, context) => {
      if (signatureResult.err) {
        signatureSubscribeResult.result = {
          isProcessed: true,
          slot: context.slot,
          error: signatureResult.err,
        };
      } else {
        signatureSubscribeResult.result = {
          isProcessed: true,
          slot: context.slot,
          error: null,
        };
      }
    },
  );

  return new Promise<SubscribeToSignatureResult>((resolve, reject) => {
    const intervalId = setInterval(() => {
      if (signatureSubscribeResult.result.isProcessed) {
        clearInterval(intervalId);
        resolve({
          kind: "SubscribeToSignature",
          result: signatureSubscribeResult.result,
        });
      }

      if (abortSignal.aborted) {
        clearInterval(intervalId);
        connection
          .removeSignatureListener(signatureSubscriptionId)
          .catch((e: unknown) => {
            console.error("Error removing signature listener", e);
          });
        reject(new Error("The operation was aborted"));
      }
    }, 100);
  });
}

interface WaitForTransactionToExpireResult {
  kind: "WaitForTransactionToExpire";
}

async function waitForTransactionToExpire(
  transaction: Transaction | VersionedTransaction,
  connection: Connection,
  abortSignal: AbortSignal,
): Promise<WaitForTransactionToExpireResult> {
  const CHECK_INTERVAL_MILLIS = 10_000;
  const blockhash =
    transaction instanceof Transaction
      ? transaction.recentBlockhash
      : transaction.message.recentBlockhash;
  if (!blockhash) {
    return { kind: "WaitForTransactionToExpire" };
  }
  while (true) {
    if (abortSignal.aborted) {
      return { kind: "WaitForTransactionToExpire" };
    }
    const isValid = await connection
      .isBlockhashValid(blockhash)
      .then((x) => x.value);
    if (!isValid) {
      return { kind: "WaitForTransactionToExpire" };
    }
    // typescript-eslint complains about the condition being always false, but it's not
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (abortSignal.aborted) {
      return { kind: "WaitForTransactionToExpire" };
    }
    await wait(CHECK_INTERVAL_MILLIS);
  }
}

interface WaitForTimeoutResult {
  kind: "WaitForTimeout";
}

async function waitForTimeout(
  timeoutMillis: number,
): Promise<WaitForTimeoutResult> {
  await wait(timeoutMillis);
  return { kind: "WaitForTimeout" };
}

function isStatusConfirmed(
  status: RpcResponseAndContext<SignatureStatus | null>,
): status is RpcResponseAndContext<SignatureStatus> {
  return status.value
    ? status.value.confirmationStatus === "finalized" ||
        status.value.confirmationStatus === "confirmed"
    : false;
}

function hasRetryAfterMs(e: unknown): e is { retryAfterMs: number } {
  if (!(e instanceof Error)) return false;
  try {
    let msg = e.message;
    msg = msg.substring(msg.indexOf("{"));
    msg = msg.substring(0, msg.lastIndexOf("}") + 1);
    const payload = JSON.parse(msg) as object | null;
    console.log("payload", payload);
    return payload !== null && "retryAfterMs" in payload;
  } catch (error) {
    return false;
  }
}

export interface WalletInfo {
  balance: number | null;
  rawBalance: string | null;
  walletAddress: string | null;
}

export interface BuildTransferTransactionParams {
  from: string;
  to: string;
  priorityFee: number;
  transfer: {
    amount: bigint;
    decimals: number;
    tokenAddress: string;
    balance: bigint;
    symbol?: string;
  };
}

export interface TokenAccountInfo {
  address?: string;
  mintAddress: string;
  tokenBalance: number;
  tokenRawBalance: string;
  decimals?: number;
}

export interface SubmitQuoteParams {
  userPublicKey: string;
  dynamicComputeUnitLimit: boolean;
  quoteResponse: Quote;
  wrapAndUnwrapSol?: boolean;
}

export interface WrapSolParams {
  amountLamports: string;
  walletAddress: string;
  prioritizationFeeLamports: string;
}

export interface UnwrapSolParams {
  walletAddress: string;
  prioritizationFeeLamports: string;
}
